import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showConfirmationModal } from "../../features/confirmationModal/ConfirmationModalSlice";
import { getLocalizedTime } from "../../utils/timeZone";
import DeleteActionIcon from "../icons/DeleteActionIcon";
import EditActionIcon from "../icons/EditActionIcon";
import TournamentTablesIcon from "../icons/TournamentTablesIcon";
import SendNotification from "../sendNotification/SendNotification";
import styles from "./oneTournament.module.css";

const OneTournament = ({
  tournamentId,
  status,
  name,
  serialNumber,
  createdBy,
  startDate,
  description,
  enDescription,
  prize,
  topPlayer,
  tournamentImage,
  prizeDescription,
  enPrizeDescription,
  playersCount,
  requiredAds,
  requiredAdsByRegion,
  stage,
  liveLink,
  gameName,
  tournamentType,
  maxUsersByTeam,
  detailsTemplate,
}) => {
  const { timeZone } = useSelector((state) => state.timeZone);
  const localizedStartDate = getLocalizedTime(startDate, timeZone);
  const { rolePermissions } = useSelector((state) => state.user);
  const isLiveTournament = status === "live" || status === "ongoing";
  const isUpcomingTournament = status === "upcoming";

  const dispatch = useDispatch();

  const getDisplayValue = (value) => {
    if (typeof value === "string") {
      return value.trim() === "" ? "-" : value;
    }

    return value === undefined || value === null ? "-" : value;
  };

  const handleDeleteTournament = () => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من حذف البطولة ؟",
        data: tournamentId,
        actionType: "deleteTournament",
      })
    );
  };

  const navigate = useNavigate();

  const handleNavigationToEditTournament = () => {
    navigate("/dashboard/tournaments/edit-tournament", {
      state: {
        tournamentId,
        status,
        name,
        createdBy,
        startDate,
        description,
        enDescription,
        prize,
        topPlayer,
        tournamentImage,
        prizeDescription,
        enPrizeDescription,
        requiredAds,
        stage,
        liveLink,
        detailsTemplate,
      },
    });
  };

  const displayedStatus =
    status === "upcoming"
      ? "قادمة"
      : status === "live"
        ? "مباشرة"
        : status === "ongoing"
          ? "جارية"
          : "منتهية";
  const displayedStage =
    stage === "finals"
      ? "نهائيات"
      : stage === "semifinals"
        ? "نصف النهائيات"
        : stage === "qualifiers"
          ? "تصفيات"
          : "-";
  const displayedTournamentType =
    tournamentType === "squad"
      ? "سكواد"
      : tournamentType === "solo"
        ? "فردي"
        : getDisplayValue(tournamentType);
  const formattedStartDate = Number.isNaN(localizedStartDate.getTime())
    ? "-"
    : localizedStartDate.toLocaleString().split(",")[0];
  const themeClassName = isLiveTournament
    ? styles.liveTheme
    : isUpcomingTournament
      ? styles.upcomingTheme
      : styles.endedTheme;
  const requiredAdsItems =
    Array.isArray(requiredAdsByRegion) && requiredAdsByRegion.length > 0
      ? requiredAdsByRegion.map((item, index) => ({
          key:
            String(item?.region?._id || item?.region || index) +
            "-" +
            String(item?.requiredAds ?? ""),
          regionName: getDisplayValue(item?.region?.name || item?.region),
          requiredAds: getDisplayValue(item?.requiredAds),
        }))
      : [
          {
            key: "default-required-ads",
            regionName: "افتراضي",
            requiredAds: getDisplayValue(requiredAds),
          },
        ];
  const detailCards = [
    {
      label: "الرقم التسلسلي",
      value: getDisplayValue(serialNumber),
    },
    {
      label: "انشأت من قبل",
      value: getDisplayValue(createdBy?.name),
    },
    {
      label: "عدد اللاعبين",
      value: getDisplayValue(playersCount),
    },
    {
      label: "الجائزة",
      value: getDisplayValue(prize),
    },
    {
      label: "تبدأ من تاريخ",
      value: formattedStartDate,
    },
  ];

  const handleNavigationToShowTables = () => {
    navigate(
      `/dashboard/tournaments/tournament-tables/${tournamentId}`,
      {
        state: {
          name,
          gameName,
          tournamentType,
          maxUsersByTeam,
        },
      }
    );
  };

  return (
    <div className={`${styles.container} ${themeClassName}`}>
      <div className={styles.heroSection}>
        <div className={styles.heroHeader}>
          <div className={styles.badgesRow}>
            <div className={styles.statusBadge}>
              {isLiveTournament && <span className={styles.liveIndicator} />}
              <span>الحالة : {displayedStatus}</span>
            </div>
            <div className={styles.metaBadge}>
              <span>اللعبة : {getDisplayValue(gameName)}</span>
            </div>
            <div className={styles.metaBadge}>
              <span>نوع البطولة : {displayedTournamentType}</span>
            </div>
            {isLiveTournament && (
              <div className={styles.metaBadge}>
                <span>المرحلة : {displayedStage}</span>
              </div>
            )}
          </div>
          <div className={styles.titleBlock}>
            <h2 className={styles.title}>{getDisplayValue(name)}</h2>
            <p className={styles.subtitle}>معلومات البطولة</p>
          </div>
        </div>
        <div className={styles.quickFactsGrid}>
          {detailCards.map((item) => (
            <div className={styles.quickFactCard} key={item.label}>
              <span className={styles.quickFactLabel}>{item.label}</span>
              <span className={styles.quickFactValue}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.contentGrid}>
        <section className={`${styles.contentPanel} ${styles.regionAdsPanel}`}>
          <span className={styles.panelLabel}>الإعلانات حسب المنطقة</span>
          <div className={styles.regionAdsList}>
            {requiredAdsItems.map((item) => (
              <div className={styles.regionAdsCard} key={item.key}>
                <span className={styles.regionAdsRegion}>{item.regionName}</span>
                <span className={styles.regionAdsValue}>
                  {item.requiredAds === "-"
                    ? "-"
                    : `${item.requiredAds} إعلان`}
                </span>
              </div>
            ))}
          </div>
        </section>
        {isLiveTournament && (
          <section className={`${styles.contentPanel} ${styles.linkPanel}`}>
            <span className={styles.panelLabel}>الرابط المباشر</span>
            {getDisplayValue(liveLink) === "-" ? (
              <p className={styles.panelValue}>-</p>
            ) : (
              <a
                href={liveLink}
                target="_blank"
                rel="noreferrer"
                className={styles.linkValue}
              >
                {liveLink}
              </a>
            )}
          </section>
        )}
      </div>
      <div className={styles.actionsSection}>
        <span className={styles.actionsTitle}>الإجراءات</span>
        <div className={styles.actionsGrid}>
          {rolePermissions.includes("delete-tournaments") && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.deleteAction}`}
              onClick={handleDeleteTournament}
            >
              <DeleteActionIcon className={styles.actionIcon} />
              <span>حذف البطولة</span>
            </button>
          )}
          {rolePermissions.includes("update-tournaments") && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.editAction}`}
              onClick={handleNavigationToEditTournament}
            >
              <EditActionIcon className={styles.actionIcon} />
              <span>التعديل على البطولة</span>
            </button>
          )}
          {rolePermissions.includes("view-tables") && (
            <button
              type="button"
              className={`${styles.actionButton} ${styles.tableAction}`}
              onClick={handleNavigationToShowTables}
            >
              <TournamentTablesIcon className={styles.actionIcon} />
              <span>جداول البطولة</span>
            </button>
          )}
          {rolePermissions.includes("send-tournament-notification") && (
            <div className={styles.notificationAction}>
              <SendNotification
                targetType={"tournament"}
                type={"tournament"}
                targetId={tournamentId}
                contextTournamentId={tournamentId}
                contextTournamentName={name}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OneTournament;
